import { UsageService } from '../../../core/usage/usage.service';
import { SpeechRecognitionService } from './../../../core/services/speech-recognition.service';
import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, takeUntil, combineLatest } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { HospitalService } from './../../../core/services/hospital.service';
import { PatientService } from './../../../core/services/patient.service';
import { GroupService } from './../../../core/services/group.service';
import { ScrollPositionService } from './../../../core/services/scroll-position.service';
import { StateCacheService } from './../../../core/services/state-cache.service';
import * as _ from 'lodash';
//import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { TutorialSection } from 'src/app/core/tutorial/tutorial.models';
import { filter, map } from 'rxjs/operators';
import { UserService } from 'src/app/core/services/user.service';
import { PatientDuplicateMatchService } from 'src/app/core/services/patient-duplicate-match.service';
import { FileDownloadService } from 'src/app/core/services/file-download.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

/** A single tab in the patient list. `key` is what gets persisted and what the toolbar branches on. */
export interface PatientListTab {
  key: string;
  label: string;
  count: number;
}

/** Fallback when the persisted tab key is unknown or no longer visible. */
const DEFAULT_TAB_KEY = 'opd';

/** stateCache key. Holds the tab KEY, never an index — tab positions shift as tabs are added. */
const TAB_CACHE_KEY = 'patientsTab';

@Component({
  selector: 'app-patient-list',
  templateUrl: './patient-list.component.html',
  styleUrls: ['./patient-list.component.scss']
})
export class PatientListComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);


  filterValue: string = '';
  hasHospitals: boolean = true;
  isDoctor: boolean = false;

  /** Sort: 'surname' | 'firstName' | 'patientNumber'. Default surname A→Z. */
  sortBy: 'surname' | 'firstName' | 'patientNumber' = 'surname';
  sortDirection: 'asc' | 'desc' = 'asc';

  totalOpd?: number;
  totalInpatient?: number;
  totalInvite?: number;
  totalAlert?: number;
  totalArchive?: number;
  totalDuplicate?: number;
  totalDeceased?: number;

  selectedTabId: number = 0;
  selectedTab: string = DEFAULT_TAB_KEY;

  // Export loading state
  isExportingPatients: boolean = false;
  exportLoadingMessage: string = 'Generating patient export...';

  private userInitialized: boolean = false;
  private hospitalsInitialized: boolean = false;
  private selectedGroup: { userRole?: string } | null = null;


  constructor(
    private router: Router,
    private userService: UserService,
    private patientService: PatientService,
    private hospitalService: HospitalService,
    private stateCache: StateCacheService,
    //private tutorialService: TutorialService,
    private scrollPositionService: ScrollPositionService,
      private groupService: GroupService,
    private duplicateMatchService: PatientDuplicateMatchService,
    private fileDownloadService: FileDownloadService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.isDoctor = user?.userType === 'doctor';
        this.userInitialized = true;

        if (this.hospitalsInitialized) {
          this.syncSelectedTab();
        }
      });

    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        // `hospitals?.length !== 0` was TRUE for `null`, and `null` is what `hospitals$` holds
        // until its request lands — optional chaining yields `undefined`, and `undefined !== 0`.
        //
        // So during the load window this claimed the practice has hospitals, the Inpatients tab
        // rendered and (for a doctor, where it is the FIRST tab) was selected — while
        // `inpatient-list` had the same `null` and could group nobody. The result was a tab that
        // rendered absolutely nothing: no ward headers, and not even the "No patients found"
        // message, because that sits behind `*ngIf="filteredInpatients.length > 0"` which was
        // true. Switching tabs "fixed" it only because the request had landed by then.
        //
        // Three states, not two: not-answered-yet (`null`), none (`[]`), some. Only the third
        // may show the tab.
        this.hasHospitals = Array.isArray(hospitals) && hospitals.length > 0;
        this.hospitalsInitialized = true;

        if (this.userInitialized) {
          this.syncSelectedTab();
        }
      });

    this.patientService.outpatients$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(outpatients => {
        this.totalOpd = outpatients?.length ?? 0;
      });

    this.patientService.inpatients$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(inpatients => {
        this.totalInpatient = inpatients?.length ?? 0;
        setTimeout(() => {
          this.scrollPositionService.restoreScrollPosition('inpatientList', 'inpatient-content');
        })
      });

    this.patientService.invites$
      .pipe(takeUntil(this.destroy$))
      .subscribe(invites => {
        this.totalInvite = invites?.length ?? 0;
      });

    this.patientService.alerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(alerts => {
        this.totalAlert = alerts?.length ?? 0;
      });

    this.patientService.archives$
      .pipe(takeUntil(this.destroy$))
      .subscribe(archives => {
        this.totalArchive = archives?.length ?? 0;
      });

    this.patientService.deceased$
      .pipe(takeUntil(this.destroy$))
      .subscribe(deceased => {
        this.totalDeceased = deceased?.length ?? 0;
      });

    this.duplicateMatchService.duplicatePairs$
      .pipe(
        takeUntil(this.destroy$),
        map(rows => rows?.length ?? 0)
      )
      .subscribe(count => {
        this.totalDuplicate = count;
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        this.selectedGroup = group ?? null;
      });
  }

  get isGroupAdmin(): boolean {
    return this.selectedGroup?.userRole?.toLowerCase() === 'admin';
  }

  /**
   * The tabs currently on screen, in display order.
   * Doctors with hospitals lead with Inpatients; everyone else leads with Outpatients.
   * Inpatients only exists when the practice has hospitals.
   */
  get visibleTabs(): PatientListTab[] {
    const opd: PatientListTab = { key: 'opd', label: 'Outpatients', count: this.totalOpd || 0 };
    const inpatient: PatientListTab = { key: 'inpatient', label: 'Inpatients', count: this.totalInpatient || 0 };

    const tabs: PatientListTab[] = this.hasHospitals
      ? (this.isDoctor ? [inpatient, opd] : [opd, inpatient])
      : [opd];

    // A state unit adds patients by hand only, so it has no invites.
    if (!this.stateMode) tabs.push({ key: 'invite', label: 'Invites', count: this.totalInvite || 0 });
    tabs.push(
      { key: 'alert', label: 'File Alerts', count: this.totalAlert || 0 },
      { key: 'duplicate', label: 'Duplicate Alerts', count: this.totalDuplicate || 0 },
      { key: 'deceased', label: 'Deceased', count: this.totalDeceased || 0 },
      { key: 'archive', label: 'Archives', count: this.totalArchive || 0 }
    );

    return tabs;
  }

  /** A state unit (tasks/state-mode.md): nothing reaches outside the system. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  trackByTabKey(_index: number, tab: PatientListTab): string {
    return tab.key;
  }

  /** OPD / inpatient tabs only (not invites, alerts, duplicates, deceased, archives). */
  get isClinicalPatientListTab(): boolean {
    return this.selectedTab === 'opd' || this.selectedTab === 'inpatient';
  }

  /** Full patient list export: practice admins only, on OPD or inpatient tab. */
  get allowExportPatients(): boolean {
    return this.isGroupAdmin && this.isClinicalPatientListTab;
  }

  /** Add patient hidden on alert, duplicate, deceased, and archive tabs. */
  get allowAddPatient(): boolean {
    return !['alert', 'duplicate', 'deceased', 'archive'].includes(this.selectedTab);
  }

  ngOnInit(): void {
    // The cache holds the tab KEY; the index is derived from it once the visible tabs are known.
    const cachedTab = this.stateCache.get<string>(TAB_CACHE_KEY);
    this.selectedTab = typeof cachedTab === 'string' ? cachedTab : DEFAULT_TAB_KEY;
    this.syncSelectedTab();

    // Defensive: ensure patients are loaded after login when navigating quickly
    combineLatest([
      this.groupService.selectedGroup$,
      this.patientService.patients$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([group, patients]) => {
        if (group && (!patients || patients.length === 0) && !this.patientService.isLoading) {
          // Fire-and-forget; PatientService will populate its subjects
          this.patientService.loadPatients().subscribe();
        }
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    //this.tutorialService.closeTutorial();
    this.stateCache.set(TAB_CACHE_KEY, this.selectedTab);
    this.scrollPositionService.saveScrollPosition('inpatientList', 'inpatient-content');
  }

  onTabChange(index: number) {
    const tabs = this.visibleTabs;
    this.selectedTab = tabs[index]?.key ?? DEFAULT_TAB_KEY;
    this.syncSelectedTab();
    this.stateCache.set(TAB_CACHE_KEY, this.selectedTab);
  }

  /**
   * Resolves `selectedTab` (the key) to `selectedTabId` (the index) against the tabs currently
   * visible. Falls back to the first tab when the key is unknown or no longer visible — e.g. the
   * user was on Inpatients and the practice lost its hospitals, or a stale key survived a deploy.
   */
  private syncSelectedTab(): void {
    const tabs = this.visibleTabs;
    const index = tabs.findIndex(tab => tab.key === this.selectedTab);
    this.selectedTabId = index === -1 ? 0 : index;
    this.selectedTab = tabs[this.selectedTabId]?.key ?? DEFAULT_TAB_KEY;
  }

  applyFilter(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value.toLowerCase();
  }

  setSort(by: 'surname' | 'firstName' | 'patientNumber', direction: 'asc' | 'desc') {
    this.sortBy = by;
    this.sortDirection = direction;
  }


  addPatient(type?: string, quick?: boolean) {
    this.usage.flow('new patient', 'start');

    this.patientService.getPatientById(null)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['add'],
          }
        }], { queryParams: { type: this.stateMode && (type || this.selectedTab) === 'invite' ? 'opd' : type || this.selectedTab, quick: quick || false } });
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error creating the patient. Please try again.')
      });
  }

  exportPatients() {
    this.groupService.selectedGroup$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(group => {
        if (!group) {
          this.showToastMessage('No practice selected. Please select a practice first.');
          return;
        }

        // Show loading overlay
        this.isExportingPatients = true;
        this.exportLoadingMessage = 'Generating patient export...';

        this.patientService.exportPatientsToExcel(group.id)
          .pipe(
            takeUntil(this.destroy$)
          )
          .subscribe({
            next: async (blob: Blob) => {
              const now = new Date();
              const dateStr = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
              const fileName = `patients_export_${dateStr}.xlsx`;
              await this.fileDownloadService.download(blob, fileName);
              this.isExportingPatients = false;
              this.showToastMessage('Patient export completed successfully.');
            },
            error: (error) => {
              console.error('Error exporting patients:', error);
              // Hide loading overlay
              this.isExportingPatients = false;
              this.showToastMessage('There was an error exporting patients. Please try again.');
            }
          });
      });
  }

}